// The period strip: the same facts over named windows.
//
// Fetched separately and rendered when it arrives — signals.device_period_metrics
// spans ninety days to reach M-2 and re-derives reach and duration medians per
// window, so it costs about three times the day summary. Blocking the page on
// a strip below the fold would be the wrong trade.
//
// A TABLE, not tiles. Twelve windows × fifteen measures is a comparison, and a
// comparison wants columns that line up — the whole point is reading CPM down
// the page and seeing that it barely moves while empty-rate does.
//
// Nothing here is computed. Every figure comes from the function, including
// the rates: a weekly CPM is total rupees over total billable, and if this
// component averaged the daily ones it would quietly publish a number nobody
// could reproduce from the ledger below.

import React from 'react';
import { Loading, ErrorState } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import s from './Periods.module.css';

const n = (v) => (v == null ? '—' : Number(v).toLocaleString());
const money = (v, dp = 2) => (v == null ? '—'
  : `₹${Number(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`);
// Never a bare "m": beside a Km column it reads as metres — it was read as
// exactly that. Minutes are always "min".
const mins = (sec) => {
  if (sec == null) return '—';
  const m = Math.round(sec / 60);
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
};

const COLS = [
  { key: 'plays', label: 'Plays', fmt: (p) => n(p.plays),
    title: 'Every creative play in the window, from impression_batches. Not what the advertiser is charged for.' },
  { key: 'billable', label: 'Advertiser-facing', fmt: (p) => n(p.billable), tone: 'good',
    title: 'Plays counting towards an advertiser under billing’s own gate. This is what Brands Manager shows.' },
  { key: 'rupees', label: 'Accrued', fmt: (p) => money(p.rupees),
    title: 'Money attached to this device’s batches in the window.' },
  { key: 'cpm', label: 'CPM', fmt: (p) => money(p.cpm), tone: 'good',
    title: 'Total rupees over total advertiser-facing impressions × 1000 — computed from the window’s sums, NOT as an average of daily CPMs, which would weight a quiet day equally with a busy one.' },
  { key: 'cpm_all_plays', label: 'CPM · all', fmt: (p) => money(p.cpm_all_plays),
    title: 'The same mille over every play, charged or not. The gap from CPM is what the presence gate withholds.' },
  { key: 'occupied', label: 'Aboard', fmt: (p) => n(p.occupied),
    title: 'Plays the presence sweep confirmed had a passenger. The other half of the empty rate\u2019s denominator.' },
  { key: 'empty_rate_pct', label: 'Empty', fmt: (p) => (p.empty_rate_pct == null ? '\u2014' : `${p.empty_rate_pct}%`), tone: 'warn',
    title: 'Share of VERDICTED plays with nobody aboard \u2014 empty over (empty + aboard). Blank, not zero, on windows where nothing was verdicted: before the gate went live on 28 Aug no play was measured, and reporting 0% there claimed an empty rate nobody had established (V317).' },
  { key: 'verdict_coverage_pct', label: 'Coverage', fmt: (p) => (p.verdict_coverage_pct == null ? '—' : `${p.verdict_coverage_pct}%`),
    title: 'Share of plays that have a verdict at all. Low on today because the sweep runs two hours behind; zero before the gate.' },
  { key: 'unique_reach', label: 'Profiles', fmt: (p) => n(p.unique_reach),
    title: 'Distinct identity profiles, RE-DERIVED per window — a person seen on two days is one profile over the week, so this is deliberately less than the sum of the daily figures. Still profiles, not people: within-ride fragmentation inflates it.' },
  { key: 'cost_per_unique_reach', label: '₹ / profile', fmt: (p) => money(p.cost_per_unique_reach),
    title: 'Rupees per distinct profile. A lower bound on cost per person, since profiles fragment.' },
  { key: 'rides', label: 'Rides', fmt: (p) => n(p.rides), tone: 'good',
    title: 'Presence islands clearing the 120 s ride floor.' },
  { key: 'ride_median_s', label: 'Ride median', fmt: (p) => mins(p.ride_median_s),
    title: 'Median ride duration, re-derived over the window — a median of daily medians is not a median.' },
  { key: 'billable_per_ride', label: 'Billable / ride', fmt: (p) => (p.billable_per_ride == null ? '—' : p.billable_per_ride),
    title: 'Advertiser-facing plays per ride. High by construction on a looping screen.' },
  { key: 'trips', label: 'Trips', fmt: (p) => n(p.trips),
    title: 'GPS trip segments. Independent of presence — a cab can drive empty.' },
  { key: 'km', label: 'Km', fmt: (p) => (p.km == null ? '—' : Number(p.km).toLocaleString()),
    title: 'Distance driven in the window.' },
  { key: 'trip_median_s', label: 'Trip median', fmt: (p) => mins(p.trip_median_s),
    title: 'Median trip duration, re-derived over the window.' },
  { key: 'rides_per_trip', label: 'Rides / trip', fmt: (p) => (p.rides_per_trip == null ? '—' : p.rides_per_trip),
    title: 'Well under 1 means the cab moved without detecting passengers — normal when deadheading, worth a look if it persists.' },
  { key: 'speech_live_pct', label: 'Live speech', fmt: (p) => (p.speech_live_pct == null ? '—' : `${p.speech_live_pct}%`),
    title: 'Share of analysed audio the gate called live in-cab speech. Blank before the gate went live on 28 Aug.' },
];

// The three families read differently and should look it.
const D = (iso) => new Date(`${String(iso).slice(0, 10)}T00:00:00`);
const fmtRange = (from, to) => {
  if (!from || !to) return '';
  const f = D(from), t = D(to);
  if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return '';
  const day = (d) => d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  if (String(from).slice(0, 10) === String(to).slice(0, 10)) return day(f);
  const sameMonth = f.getMonth() === t.getMonth() && f.getFullYear() === t.getFullYear();
  const mon = (d) => d.toLocaleDateString(undefined, { month: 'short' });
  return sameMonth
    ? `${f.getDate()}–${t.getDate()} ${mon(t)}`
    : `${day(f)} – ${day(t)}`;
};

const BAND = { D: 'day', W: 'week', M: 'month' };
const bandOf = (k) => (k.startsWith('D') ? BAND.D : k.startsWith('W') ? BAND.W : BAND.M);

export default function Periods({ thingName }) {
  const { data, error, loading, reload } = useApi(
    () => api.get(`/ggos-devices/${encodeURIComponent(thingName)}/ledger/periods`)
            .then((r) => r.data?.data), [thingName]);

  if (loading) return <div className={s.wrap}><Loading what="the period comparison" /></div>;
  if (error) return <div className={s.wrap}><ErrorState error={error} onRetry={reload} /></div>;

  const periods = data?.periods || [];
  if (!periods.length) return null;

  return (
    <div className={s.wrap}>
      <div className={s.head}>
        <span className={s.title}>Across periods</span>
        <span className={s.sub}>
          rates recomputed per window, never averaged from days — reach and medians re-derived
        </span>
      </div>
      <div className={s.scroll}>
        <table className={s.table}>
          <thead>
            <tr>
              <th className={s.stick}>Window</th>
              <th className={s.num}>Days</th>
              {COLS.map((c) => (
                <th key={c.key} className={s.num} title={c.title}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {periods.map((p) => (
              <tr key={p.period_key} className={s[bandOf(p.period_key)]}>
                <th className={s.stick} scope="row">
                  <span className={s.pKey}>{p.period_key}</span>
                  <span className={s.pLabel}>{p.period_label}</span>
                  {/* The range itself, visible — "Last 7 days" only makes
                      sense when you can see WHICH seven. */}
                  <span className={s.pRange}>{fmtRange(p.from_date, p.to_date)}</span>
                </th>
                <td className={s.num}>{p.active_days || '—'}</td>
                {COLS.map((c) => (
                  <td key={c.key} className={`${s.num} ${c.tone ? s[c.tone] : ''}`}>{c.fmt(p)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
